import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Plus, Check, X, Trash2, Pencil, Search } from 'lucide-react';
import dayjs from 'dayjs';
import {
  CreateMaterialRequestPayload,
  MaterialRequest,
  MaterialRequestsApi,
  UpdateMaterialRequestPayload,
} from '@/services/material-requests.api';
import { MaterialItem, MaterialItemsApi } from '@/services/material-items.api';
import { SchoolsApi } from '@/services/superadmin.api';
import { OrgPortalApi } from '@/services/org.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';

const STATUS_TONE: Record<MaterialRequest['status'], 'amber' | 'green' | 'red'> = {
  pending: 'amber',
  approved: 'green',
  rejected: 'red',
};

const columnHelper = createColumnHelper<MaterialRequest>();

export function MaterialRequestsPage({ scope }: { scope: 'superadmin' | 'organization' | 'school' }) {
  const qc = useQueryClient();
  const showSchoolPicker = scope !== 'school';
  const canApproveReject = scope === 'superadmin' || scope === 'organization';
  const canCreate = scope === 'organization' || scope === 'school';
  const [modal, setModal] = useState(false);
  const [editTarget, setEditTarget] = useState<MaterialRequest | null>(null);
  const [confirm, setConfirm] = useState<{ open: boolean; request?: MaterialRequest }>({ open: false });
  const [schoolId, setSchoolId] = useState('');
  const [status, setStatus] = useState<MaterialRequest['status'] | ''>('');
  const [errorNotice, setErrorNotice] = useState<string | null>(null);

  const dt = useDataTable({ defaultLimit: 20 });

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, status]);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-material-filter', scope],
    queryFn: async () =>
      scope === 'organization' ? (await OrgPortalApi.listSchools({ limit: 100 })).items : (await SchoolsApi.list({ limit: 100 })).items,
    enabled: showSchoolPicker,
  });

  const { data, isFetching } = useQuery({
    queryKey: ['material-requests', { ...dt.params, schoolId, status, scope }],
    queryFn: () =>
      MaterialRequestsApi.list({
        ...dt.params,
        schoolId: showSchoolPicker ? schoolId || undefined : undefined,
        status: status || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const create = useMutation({
    mutationFn: (payload: CreateMaterialRequestPayload) => MaterialRequestsApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-requests'] });
      setModal(false);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMaterialRequestPayload }) =>
      MaterialRequestsApi.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-requests'] });
      setEditTarget(null);
    },
  });

  const approve = useMutation({
    mutationFn: (id: string) => MaterialRequestsApi.approve(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['material-requests'] }),
    onError: (e) => setErrorNotice(errMsg(e) ?? 'Could not approve'),
  });
  const reject = useMutation({
    mutationFn: (id: string) => MaterialRequestsApi.reject(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['material-requests'] }),
    onError: (e) => setErrorNotice(errMsg(e) ?? 'Could not reject'),
  });
  const removeRequest = useMutation({
    mutationFn: (id: string) => MaterialRequestsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-requests'] });
      setConfirm({ open: false });
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'serial',
        header: 'S.No',
        enableSorting: false,
        cell: (info) => (dt.page - 1) * dt.limit + info.row.index + 1,
      }),
      columnHelper.accessor('actionType', {
        id: 'actionType',
        header: 'Type',
        cell: (info) => <span className="capitalize">{info.getValue()}</span>,
      }),
      columnHelper.display({
        id: 'items',
        header: 'Items',
        cell: (info) => (
          <div className="max-w-xs text-xs text-slate-600">
            {info.row.original.items
              .map((i) => `${i.materialItem?.name ?? '—'} × ${Number(i.qty)}${i.unit}`)
              .join(', ')}
          </div>
        ),
      }),
      ...(showSchoolPicker
        ? [
            columnHelper.display({
              id: 'school',
              header: 'School',
              cell: (info: any) => info.row.original.school?.name ?? '—',
            }),
          ]
        : []),
      columnHelper.accessor('sessionYear', { id: 'sessionYear', header: 'Session' }),
      columnHelper.accessor('status', {
        id: 'status',
        header: 'Status',
        cell: (info) => <Badge tone={STATUS_TONE[info.getValue()]}>{info.getValue()}</Badge>,
      }),
      columnHelper.accessor('createdAt', {
        id: 'createdAt',
        header: 'Created',
        cell: (info) => dayjs(info.getValue()).format('DD MMM YYYY'),
      }),
      columnHelper.display({
        id: 'createdByName',
        header: 'Requested By',
        cell: (info) => info.row.original.createdByName ?? '—',
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        // Real legacy: owner school or Super Admin/org admin can edit or
        // delete a request at ANY status (not just pending), and an admin
        // can freely re-transition status back and forth — no "already
        // approved, cannot transition again" restriction. See
        // material-requests.service.ts's update()/transition() doc comments.
        cell: (info) => {
          const r = info.row.original;
          return (
            <div className="inline-flex items-center gap-1">
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
                onClick={() => setEditTarget(r)}
                title="Edit"
              >
                <Pencil className="h-4 w-4" />
              </button>
              {canApproveReject && (
                <>
                  <button
                    className="rounded-md p-1.5 text-slate-500 hover:bg-green-50 hover:text-green-600 disabled:opacity-50"
                    onClick={() => approve.mutate(r.id)}
                    disabled={approve.isPending || r.status === 'approved'}
                    title="Approve"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    onClick={() => reject.mutate(r.id)}
                    disabled={reject.isPending || r.status === 'rejected'}
                    title="Reject"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </>
              )}
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                onClick={() => setConfirm({ open: true, request: r })}
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showSchoolPicker, canApproveReject, approve.isPending, reject.isPending, dt.page, dt.limit],
  );

  return (
    <>
      <PageHeader
        title="Material Requests"
        description={
          scope === 'superadmin'
            ? 'All schools — approve or reject pending requests.'
            : scope === 'organization'
              ? 'Your schools — request materials on their behalf, or approve/reject pending requests.'
              : 'Request materials from Head Office, or return unused ones.'
        }
        actions={
          canCreate && (
            <button className="btn-primary" onClick={() => setModal(true)}>
              <Plus className="mr-1.5 h-4 w-4" /> New Request
            </button>
          )
        }
      />

      {errorNotice && (
        <div className="mb-4 flex items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>{errorNotice}</span>
          <button onClick={() => setErrorNotice(null)} className="text-red-600 hover:text-red-800">
            ✕
          </button>
        </div>
      )}

      <DataTable
        columns={columns}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No material requests yet."
        toolbar={
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={dt.searchInput}
                onChange={(e) => dt.setSearchInput(e.target.value)}
                placeholder="Search notes, item, school…"
                className="pl-9"
              />
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <Select value={status} onChange={(e) => setStatus(e.target.value as any)} className="sm:w-44">
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </Select>
              {showSchoolPicker && (
                <Select value={schoolId} onChange={(e) => setSchoolId(e.target.value)} className="sm:w-56">
                  <option value="">All schools</option>
                  {schools.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              )}
            </div>
          </div>
        }
      />

      {canCreate && (
        <CreateRequestModal
          open={modal}
          onClose={() => setModal(false)}
          onSubmit={(payload) => create.mutate(payload)}
          saving={create.isPending}
          errorMsg={errMsg(create.error)}
          schools={scope === 'organization' ? schools : undefined}
        />
      )}

      <ConfirmDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        onConfirm={() => confirm.request && removeRequest.mutate(confirm.request.id)}
        loading={removeRequest.isPending}
        title="Delete request?"
        message="This removes the request entirely, regardless of its current status."
        confirmText="Delete"
      />

      {editTarget && (
        <EditRequestModal
          request={editTarget}
          onClose={() => setEditTarget(null)}
          onSubmit={(payload) => update.mutate({ id: editTarget.id, payload })}
          saving={update.isPending}
          errorMsg={errMsg(update.error)}
        />
      )}
    </>
  );
}

function CreateRequestModal({
  open,
  onClose,
  onSubmit,
  saving,
  errorMsg,
  schools,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateMaterialRequestPayload) => void;
  saving: boolean;
  errorMsg?: string;
  /** Org Admin only — when set, shows a school picker and requires a selection. */
  schools?: { id: string; name: string }[];
}) {
  const [actionType, setActionType] = useState<'purchase' | 'return'>('purchase');
  const [notes, setNotes] = useState('');
  const [reqSchoolId, setReqSchoolId] = useState('');
  const [lines, setLines] = useState<{ materialItemId: string; qty: number }[]>([
    { materialItemId: '', qty: 1 },
  ]);

  const { data: catalog = [] } = useQuery({
    queryKey: ['material-items-active'],
    queryFn: MaterialItemsApi.listActive,
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setActionType('purchase');
      setNotes('');
      setReqSchoolId('');
      setLines([{ materialItemId: '', qty: 1 }]);
    }
  }, [open]);

  const catalogById = new Map(catalog.map((c) => [c.id, c]));
  const valid =
    lines.length > 0 &&
    lines.every((l) => l.materialItemId && l.qty > 0) &&
    (!schools || !!reqSchoolId);

  const submit = () => {
    onSubmit({
      actionType,
      notes: notes.trim() || undefined,
      items: lines.map((l) => ({ materialItemId: l.materialItemId, qty: l.qty })),
      schoolId: schools ? reqSchoolId : undefined,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Material Request"
      size="lg"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={saving || !valid}>
            {saving ? 'Submitting…' : 'Submit request'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {schools && (
          <Field label="School" required>
            <Select value={reqSchoolId} onChange={(e) => setReqSchoolId(e.target.value)}>
              <option value="">— Select school —</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Type" required>
          <Select value={actionType} onChange={(e) => setActionType(e.target.value as 'purchase' | 'return')}>
            <option value="purchase">Purchase</option>
            <option value="return">Return</option>
          </Select>
        </Field>

        <div>
          <span className="mb-2 block text-sm font-medium text-slate-700">Items</span>
          <div className="space-y-2">
            {lines.map((line, idx) => (
              <div key={idx} className="flex gap-2">
                <Select
                  className="flex-1"
                  value={line.materialItemId}
                  onChange={(e) =>
                    setLines((prev) =>
                      prev.map((p, i) => (i === idx ? { ...p, materialItemId: e.target.value } : p)),
                    )
                  }
                >
                  <option value="">— Select item —</option>
                  {catalog.map((c: MaterialItem) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.unit})
                    </option>
                  ))}
                </Select>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  className="w-32"
                  value={line.qty}
                  onChange={(e) =>
                    setLines((prev) =>
                      prev.map((p, i) => (i === idx ? { ...p, qty: Number(e.target.value) || 0 } : p)),
                    )
                  }
                />
                <span className="flex items-center text-sm text-slate-500">
                  {catalogById.get(line.materialItemId)?.unit ?? ''}
                </span>
                {lines.length > 1 && (
                  <button
                    type="button"
                    className="rounded-md p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="btn-secondary text-xs"
              onClick={() => setLines((prev) => [...prev, { materialItemId: '', qty: 1 }])}
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Add another item
            </button>
          </div>
        </div>

        <Field label="Notes (optional)">
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>

        {errorMsg && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorMsg}</div>}
      </div>
    </Modal>
  );
}

function EditRequestModal({
  request,
  onClose,
  onSubmit,
  saving,
  errorMsg,
}: {
  request: MaterialRequest;
  onClose: () => void;
  onSubmit: (payload: UpdateMaterialRequestPayload) => void;
  saving: boolean;
  errorMsg?: string;
}) {
  const [actionType, setActionType] = useState<'purchase' | 'return'>(request.actionType);
  const [notes, setNotes] = useState(request.notes ?? '');
  const [lines, setLines] = useState<{ materialItemId: string; qty: number }[]>(
    request.items.map((i) => ({ materialItemId: i.materialItemId, qty: Number(i.qty) })),
  );

  const { data: catalog = [] } = useQuery({
    queryKey: ['material-items-active'],
    queryFn: MaterialItemsApi.listActive,
  });

  const catalogById = new Map(catalog.map((c) => [c.id, c]));
  const valid = lines.length > 0 && lines.every((l) => l.materialItemId && l.qty > 0);

  const submit = () => {
    onSubmit({
      actionType,
      notes: notes.trim() || undefined,
      items: lines.map((l) => ({ materialItemId: l.materialItemId, qty: l.qty })),
    });
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit Material Request"
      size="lg"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={saving || !valid}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Type" required>
          <Select value={actionType} onChange={(e) => setActionType(e.target.value as 'purchase' | 'return')}>
            <option value="purchase">Purchase</option>
            <option value="return">Return</option>
          </Select>
        </Field>

        <div>
          <span className="mb-2 block text-sm font-medium text-slate-700">Items</span>
          <div className="space-y-2">
            {lines.map((line, idx) => (
              <div key={idx} className="flex gap-2">
                <Select
                  className="flex-1"
                  value={line.materialItemId}
                  onChange={(e) =>
                    setLines((prev) =>
                      prev.map((p, i) => (i === idx ? { ...p, materialItemId: e.target.value } : p)),
                    )
                  }
                >
                  <option value="">— Select item —</option>
                  {catalog.map((c: MaterialItem) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.unit})
                    </option>
                  ))}
                </Select>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  className="w-32"
                  value={line.qty}
                  onChange={(e) =>
                    setLines((prev) =>
                      prev.map((p, i) => (i === idx ? { ...p, qty: Number(e.target.value) || 0 } : p)),
                    )
                  }
                />
                <span className="flex items-center text-sm text-slate-500">
                  {catalogById.get(line.materialItemId)?.unit ?? ''}
                </span>
                {lines.length > 1 && (
                  <button
                    type="button"
                    className="rounded-md p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="btn-secondary text-xs"
              onClick={() => setLines((prev) => [...prev, { materialItemId: '', qty: 1 }])}
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Add another item
            </button>
          </div>
        </div>

        <Field label="Notes (optional)">
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>

        {errorMsg && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorMsg}</div>}
      </div>
    </Modal>
  );
}

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}
